import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Check, X } from 'lucide-react';

interface PhotoCropModalProps {
  file: File;
  onCrop: (croppedFile: File) => void;
  onCancel: () => void;
}

const OUTPUT_SIZE = 400; // Output image size in pixels

export default function PhotoCropModal({ file, onCrop, onCancel }: PhotoCropModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const [loaded, setLoaded] = useState(false);

  // Load the selected image
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      // Fit the image so its shortest side fills the circle
      const minDim = Math.min(img.width, img.height);
      const initialScale = 300 / minDim;
      setScale(initialScale);
      setOffset({
        x: (300 - img.width * initialScale) / 2,
        y: (300 - img.height * initialScale) / 2,
      });
      setLoaded(true);
    };
    img.src = URL.createObjectURL(file);
    return () => URL.revokeObjectURL(img.src);
  }, [file]);

  // Draw the preview
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, 300, 300);

    // Draw the image
    ctx.save();
    ctx.beginPath();
    ctx.arc(150, 150, 150, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, offset.x, offset.y, img.width * scale, img.height * scale);
    ctx.restore();

    // Draw circular border
    ctx.beginPath();
    ctx.arc(150, 150, 149, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Dim corners outside circle
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, 300, 300);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(150, 150, 150, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }, [offset, scale]);

  useEffect(() => {
    if (loaded) draw();
  }, [loaded, draw]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setOffset({
      x: dragStart.current.ox + (e.clientX - dragStart.current.x),
      y: dragStart.current.oy + (e.clientY - dragStart.current.y),
    });
  };

  const handleMouseUp = () => setDragging(false);

  const handleCrop = () => {
    const img = imgRef.current;
    if (!img) return;

    const out = document.createElement('canvas');
    out.width = OUTPUT_SIZE;
    out.height = OUTPUT_SIZE;
    const ctx = out.getContext('2d');
    if (!ctx) return;

    // Scale factor from preview (300px) to output
    const ratio = OUTPUT_SIZE / 300;

    // Clip to circle
    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();

    ctx.drawImage(
      img,
      offset.x * ratio,
      offset.y * ratio,
      img.width * scale * ratio,
      img.height * scale * ratio,
    );

    out.toBlob((blob) => {
      if (!blob) return;
      const cropped = new File([blob], file.name.replace(/\.\w+$/, '.png'), {
        type: 'image/png',
      });
      onCrop(cropped);
    }, 'image/png');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="crop-modal">
      <div className="bg-card rounded-xl shadow-xl p-6 max-w-sm w-full space-y-4">
        <h3 className="text-sm font-semibold">Crop Profile Photo</h3>
        <p className="text-xs text-muted-foreground">Drag to reposition. Use zoom to resize.</p>

        <div className="flex justify-center">
          <canvas
            ref={canvasRef}
            width={300}
            height={300}
            className="rounded-full cursor-grab active:cursor-grabbing"
            style={{ touchAction: 'none' }}
            data-testid="crop-canvas"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
        </div>

        <div className="flex items-center justify-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setScale((s) => Math.max(0.1, s * 0.85))} data-testid="crop-zoom-out">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground w-12 text-center">{Math.round(scale * 100)}%</span>
          <Button variant="ghost" size="sm" onClick={() => setScale((s) => s * 1.15)} data-testid="crop-zoom-in">
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="crop-cancel">
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={handleCrop} data-testid="crop-save">
            <Check className="h-4 w-4 mr-1" /> Save
          </Button>
        </div>
      </div>
    </div>
  );
}
